import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import FIELD_OPTIONS, {
  OptionType,
} from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SearchPhoneNumbers/fieldOptions';
import PaginateLight from '@ad-scripts/presentation/components/common/paginate_light';
import Table from 'src/presentation/components/pages/shared/Table';
import availableNumbersResource from 'src/actions/availableNumbers/availableNumbersResource';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { EmptyStateView, CapabilitiesIcons, getCityWithRegion } from '../components';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { Row } from 'src/presentation/components/forms/Layout';
import { TH, TD } from '@ad-scripts/presentation/components/common/table';
import { useResourceValues, isLoadingStatus } from 'src/presentation/hooks/useResource';

type PropsType = {
  handleSelectPhone: Function;
  searchFields;
};

const columnConfig = [
  {
    attr: 'phoneNumber',
    label: 'Phone number',
    customColumnStyle: () => ({ width: '213px' }),
    customRenderer: item => (item.phoneNumber ? formatPhone(item.phoneNumber) : ''),
  },
  {
    attr: 'locality',
    label: 'City',
    customColumnStyle: () => ({ width: '219px' }),
    customRenderer: item => getCityWithRegion(item),
  },
  {
    attr: 'capabilities',
    label: 'Capabilities',
    customColumnStyle: () => ({ width: '208px' }),
    customRenderer: ({ capabilities }) => <CapabilitiesIcons capabilities={capabilities} />,
  },
];

const initialSorting = { attr: 'phoneNumber' };
const pagination = { pageSize: 5 };

export default function TablePhoneNumbers({ searchFields, handleSelectPhone }: PropsType) {
  const { values: items, loadingState } = useResourceValues(availableNumbersResource);
  const [currentPage, updatePage] = useState(1);
  const itemsPerPage = 5;
  const dataAid = 'phone-numbers';

  useEffect(() => {
    updatePage(1);
  }, [items]);

  const renderRowActions = ({ item }) => (
    <StyledButton
      data-aid={`selectPhone-${item.phoneNumber}`}
      type={Button.Types.SECONDARY}
      onClick={() => handleSelectPhone(item)}
    >
      Select number
    </StyledButton>
  );

  const renderSearchInfoCriteria = () => {
    const { numberTypeOptions, countryCodeOptions } = FIELD_OPTIONS;
    const countryCodeOption = countryCodeOptions.find(
      option => option.value === searchFields.countryCode
    ) as OptionType;
    const numberTypeOption = numberTypeOptions.find(option => option.value === searchFields.numberType) as OptionType;

    return (
      <SearchInfoCriteria>
        <StyledFontBold>{numberTypeOption.label}</StyledFontBold> numbers in{' '}
        <StyledFontBold>{countryCodeOption.label}</StyledFontBold>
      </SearchInfoCriteria>
    );
  };

  const renderContent = () => {
    const isLoading = isLoadingStatus(loadingState);
    const hasEmptyResult = Boolean(!items.length && searchFields.searchTerm && !isLoading);
    return (
      <>
        <StyledHeaderRow>
          {renderSearchInfoCriteria()}
          <PaginateLight
            currentPage={currentPage}
            list={items}
            onChange={updatePage}
            pageSize={itemsPerPage}
            showPagination
            title="numbers"
          />
        </StyledHeaderRow>
        <Table
          columnConfig={columnConfig}
          currentPage={currentPage}
          dataAid={dataAid}
          initialSorting={initialSorting}
          isLoading={isLoading}
          items={items}
          pagination={pagination}
          renderRowActions={renderRowActions}
        ></Table>
        {hasEmptyResult && <EmptyStateView searchText={searchFields.searchTerm} filterType={searchFields.filterType} />}
      </>
    );
  };

  return <StyledWrapper>{renderContent()}</StyledWrapper>;
}

const SearchInfoCriteria = styled.div`
  margin-right: ${({ theme }) => theme.spacing.medium};
`;

const StyledHeaderRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 ${({ theme }) => theme.spacing.insetLarge};
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 321px;
  max-width: 800px;
  margin-bottom: 0;

  ${TH} {
    padding: ${({ theme }) => theme.spacing.medium};
  }

  ${TD} {
    padding: ${({ theme }) => theme.spacing.medium};
  }
`;

const StyledFontBold = styled.span`
  font-weight: bold;
`;

const StyledButton = styled(Button)`
  line-height: 1.1;
`;
